import {
  BookOpenIcon,
  ClipboardListIcon,
  FolderTreeIcon,
  GaugeIcon,
  ImageIcon,
  LanguagesIcon,
  LinkIcon,
  MenuSquareIcon,
  NewspaperIcon,
  SearchCheckIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ShieldIcon,
  UsersIcon,
  FileUpIcon,
  ScrollTextIcon,
  type LucideIcon,
} from 'lucide-react';

import { PERMISSIONS, type Permission } from '@/features/auth/permissions';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Item is hidden unless the user holds at least one of these. */
  permissions: readonly Permission[];
  /** Marks routes not yet implemented, so the shell can show them as disabled. */
  planned?: boolean;
}

export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

/**
 * The sidebar, declared as data.
 *
 * Every item names the permissions that reveal it, so adding a section is one
 * entry here rather than an entry plus a conditional in the layout. Items marked
 * `planned` render disabled: an editor seeing the full map of the panel with
 * "coming soon" is less confusing than sections appearing without warning.
 *
 * Permission names match the API catalogue exactly. Where the API only has a
 * `manage` verb (navigation, settings, roles), that is what the nav checks —
 * inventing a `.view` here would hide the item from everyone who can actually
 * use the screen.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'Dashboard',
        to: '/',
        icon: GaugeIcon,
        permissions: [],
      },
    ],
  },
  {
    label: 'Content',
    items: [
      {
        label: 'Content',
        to: '/content',
        icon: NewspaperIcon,
        permissions: [PERMISSIONS.contents.view],
      },
      {
        label: 'Translations',
        to: '/translations',
        icon: ClipboardListIcon,
        // Must match the /translations route guard exactly — showing the item
        // to contents.view holders whose route requires contents.translate
        // would dead-end them on /forbidden.
        permissions: [PERMISSIONS.contents.translate],
      },
      {
        label: 'Gate board',
        to: '/gates',
        icon: ShieldCheckIcon,
        permissions: [PERMISSIONS.gates.view],
      },
      {
        label: 'Taxonomy',
        to: '/taxonomy',
        icon: FolderTreeIcon,
        permissions: [PERMISSIONS.taxonomy.view],
      },
      {
        label: 'Authors',
        to: '/authors',
        icon: BookOpenIcon,
        permissions: [PERMISSIONS.authors.view],
      },
      {
        label: 'Media',
        to: '/media',
        icon: ImageIcon,
        permissions: [PERMISSIONS.media.view],
      },
    ],
  },
  {
    label: 'Search presence',
    items: [
      {
        label: 'SEO',
        to: '/seo',
        icon: SearchCheckIcon,
        permissions: [PERMISSIONS.seo.view],
      },
      {
        label: 'Redirects',
        to: '/redirects',
        icon: LinkIcon,
        permissions: [PERMISSIONS.redirects.view],
      },
      {
        label: 'Navigation',
        to: '/navigation',
        icon: MenuSquareIcon,
        permissions: [PERMISSIONS.navigation.manage],
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'Languages',
        to: '/languages',
        icon: LanguagesIcon,
        permissions: [PERMISSIONS.locales.view],
      },
      {
        label: 'Users',
        to: '/users',
        icon: UsersIcon,
        permissions: [PERMISSIONS.users.view],
      },
      {
        label: 'Roles',
        to: '/roles',
        icon: ShieldIcon,
        permissions: [PERMISSIONS.roles.manage],
      },
      {
        label: 'Settings',
        to: '/settings',
        icon: SettingsIcon,
        permissions: [PERMISSIONS.settings.manage],
      },
      {
        label: 'Audit',
        to: '/audit',
        icon: ScrollTextIcon,
        permissions: [PERMISSIONS.audit.view],
      },
      {
        label: 'Import',
        to: '/import',
        icon: FileUpIcon,
        permissions: [PERMISSIONS.import.view],
      },
    ],
  },
];
